// src/components/ResizedImage.tsx
import React, { useEffect, useMemo, useState } from 'react'
import { buildSrcSet, buildResizedUrl, DEFAULT_WIDTHS } from '../../utils/imageUtils'

interface ResizedImageProps {
  /** original URL from frontmatter, e.g. "/uploads/img_4385.jpg" */
  src: string
  alt?: string
  className?: string
  /** how the browser should pick image sizes; default approximates a grid item */
  sizes?: string
  /** widths to include in srcset (px) */
  widths?: number[]
  /** where the resized images live, relative to site root (default '/uploads/resized') */
  resizedBasePath?: string
  /** if 'resized' will use generated files; if 'none' will use raw src without srcset */
  strategy?: 'resized' | 'none'
  /** optional tiny placeholder URL (data URL or path to tiny image) */
  placeholder?: string
  /** loading attribute for <img> ('lazy' or 'eager') */
  loading?: 'lazy' | 'eager'
  /** mark as LCP candidate to add a <link rel="preload"> for the medium fallback URL */
  preload?: boolean
  /** inline styles for image element */
  style?: React.CSSProperties
  /** image wrapper (aspect) style — recommended to keep to prevent CLS */
  wrapperStyle?: React.CSSProperties
}

/**
 * ResizedImage
 * - Uses resized outputs produced by the generator script (e.g. /uploads/resized/name-320.avif)
 * - Graceful fallback: if strategy='none' or if resized pattern is not applicable, renders raw src
 */
const ResizedImage: React.FC<ResizedImageProps> = ({
  src,
  alt = '',
  className = '',
  sizes = '(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw',
  widths = DEFAULT_WIDTHS as unknown as number[],
  resizedBasePath = '/uploads/resized',
  strategy = 'resized',
  placeholder,
  loading = 'lazy',
  preload = false,
  style,
  wrapperStyle,
}) => {
  const [loaded, setLoaded] = useState(false)
  const imgRef = React.useRef<HTMLImageElement>(null)

  const parts = useMemo(() => {
    return src ? src.split('?')[0].split('/').pop()?.match(/^(.+)\.(jpe?g|png|webp|avif)$/i) : null
  }, [src])

  const hasImageFile = Boolean(parts && parts[1] && parts[2])

  // Check if image is already loaded (handles case where image loads before onLoad handler attaches)
  useEffect(() => {
    const img = imgRef.current
    if (img && img.complete && img.naturalHeight !== 0) {
      setLoaded(true)
    }
  }, [])

  // fallback source (medium-sized jpeg)
  const fallbackSrc = useMemo(() => {
    if (strategy === 'resized' && hasImageFile) {
      // use 480 as sensible fallback
      return buildResizedUrl(src, 480, '.jpg', resizedBasePath)
    }
    // otherwise use raw src
    return src
  }, [src, strategy, resizedBasePath, hasImageFile])

  const webpSrcSet = useMemo(() => {
    if (strategy !== 'resized' || !hasImageFile) return undefined
    return buildSrcSet(src, widths, resizedBasePath, '.webp')
  }, [src, strategy, widths, resizedBasePath, hasImageFile])

  const jpegSrcSet = useMemo(() => {
    if (strategy !== 'resized' || !hasImageFile) return undefined
    return buildSrcSet(src, widths, resizedBasePath, '.jpg')
  }, [src, strategy, widths, resizedBasePath, hasImageFile])

  // Preload LCP candidate: inject link rel="preload" for the image
  // Modern browsers will use WebP from imagesrcset; older browsers fall back to href
  useEffect(() => {
    if (!preload) return
    if (typeof document === 'undefined') return
    if (strategy !== 'resized' || !webpSrcSet) return

    // Use a composite key to check for existing preload (webp srcset + fallback)
    const preloadKey = `webp-${webpSrcSet.split(',')[0]?.trim().split(' ')[0]}`
    const existingLink = document.head.querySelector(
      `link[rel="preload"][as="image"][data-preload-key="${preloadKey}"]`
    )
    if (existingLink) return // Don't create duplicate

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    // Preload WebP with srcset for modern browsers
    link.setAttribute('imagesrcset', webpSrcSet)
    link.setAttribute('imagesizes', sizes)
    link.setAttribute('type', 'image/webp')
    // Fallback href for browsers that don't support imagesrcset
    link.href = fallbackSrc
    link.setAttribute('data-preload-key', preloadKey)
    document.head.appendChild(link)

    return () => {
      // Only remove if it still exists and hasn't been claimed by another instance
      if (link.parentNode === document.head) {
        document.head.removeChild(link)
      }
    }
  }, [preload, webpSrcSet, fallbackSrc, sizes, strategy])

  // image styles and wrapper to avoid CLS
  const defaultWrapperStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    paddingTop: '100%', // aspect-square; change to e.g. '56.25%' for 16:9
    overflow: 'hidden',
    background: '#f3f4f6',
    ...wrapperStyle,
  }

  const imgStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'opacity 240ms ease, transform 240ms ease',
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'none' : 'scale(1.02)',
    borderRadius: 0,
    ...style,
  }

  return (
    <div className={`resized-image-wrapper ${className}`} style={defaultWrapperStyle}>
      {strategy === 'resized' && webpSrcSet && jpegSrcSet ? (
        <picture style={{ position: 'absolute', inset: 0 }}>
          <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
          <img
            ref={imgRef}
            src={fallbackSrc}
            alt={alt}
            loading={loading}
            decoding="async"
            style={imgStyle}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </picture>
      ) : (
        // fallback to native image URL (no srcset)
        <>
          {placeholder && !loaded ? (
            // placeholder background layer
            <img
              src={placeholder}
              alt=""
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(6px)',
                transform: 'scale(1.03)',
                transition: 'opacity 240ms ease',
                opacity: loaded ? 0 : 1,
                pointerEvents: 'none',
              }}
            />
          ) : null}
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            loading={loading}
            decoding="async"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 240ms ease, transform 240ms ease',
              transform: loaded ? 'none' : 'scale(1.02)',
              ...style,
            }}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </>
      )}
    </div>
  )
}

export default ResizedImage